<template>
    <div>
        <h3 v-if="title" v-text="title" />
        <row class="mt-3">
            <column>
                <jet-select
                    v-model="cadence.unit"
                    :horizontal="true"
                    :invalid-feedback="errors?.unit"
                    :label="trans('cadence.unit.label')"
                    :options="repeatsOptions"
                    :required="true"
                    @update:model-value="updateCadenceUnit($event)"
                />
            </column>
        </row>
        <row>
            <column>
                <jet-select
                    v-model="cadence.value"
                    :disabled="!isConfigurable"
                    :horizontal="true"
                    :invalid-feedback="errors?.value"
                    :label="trans('cadence.value.label')"
                    :options="everyOptions"
                    placeholder=""
                    @update:model-value="emitChanges"
                />
            </column>
        </row>
        <row>
            <column>
                <jet-datepicker
                    v-model="cadence.startDate"
                    :deletable="false"
                    :disabled="!isConfigurable"
                    :horizontal="true"
                    :invalid-feedback="errors?.startDate"
                    :label="trans('cadence.start_date.label')"
                    placeholder=""
                    @update:model-value="emitChanges"
                />
            </column>
        </row>
        <row>
            <column>
                <div class="d-flex justify-center align-items-center" />
            </column>
        </row>
        <row
            v-if="
                nextCadenceDateMessageTranslationKeySpecified &&
                nextDueDateRoute
            "
        >
            <column>
                <spinner v-if="isLoading" />
                <div v-else>
                    <div
                        v-if="nextCadenceDateRaw"
                        v-html="
                            trans(
                                this
                                    .nextCadenceDateMessageTranslationKeySpecified,
                                { date: formattedNextCadenceDate },
                            )
                        "
                    />
                    <div v-else v-html="errorMessage" />
                </div>
            </column>
        </row>
    </div>
</template>

<script>
import JetSelect from '@/Jetstream/Select.vue';
import JetDatepicker from '@/Jetstream/Datepicker.vue';
import { DateTime } from 'luxon';
import { $http } from '@/bootstrap.js';

export default {
    name: 'CadenceConfigForm',
    props: {
        title: String,
        modelValue: [Object, String, null],
        nextDueDateRoute: [String, null],
        nextCadenceDateMessageTranslationKeySpecified: [String, null],
        nextCadenceDateMessageTranslationKeyNever: [String, null],
        errors: [Object, null],
        updateDueDate: Boolean,
    },
    components: {
        JetDatepicker,
        JetSelect,
    },
    data() {
        return {
            isLoading: false,
            cadenceInformation: null,
            cadence: {
                unit: 'never',
                value: null,
                startDate: null,
            },
        };
    },
    computed: {
        options() {
            return this.$page.props.cadenceOptions;
        },
        repeatsOptions() {
            return this.options.map((option) => ({
                label: this.trans(`cadence.unit.${option.unit}.option`),
                value: option.unit,
            }));
        },
        everyOptions() {
            let { unit } = this.cadence;
            let range = this.options.find(
                (option) => option.unit === unit,
            )?.range;

            return range
                ? range.map((val) => ({
                      label: this.trans(`cadence.unit.${unit}.value`, {
                          '%d': val,
                      }),
                      value: val.toString(),
                  }))
                : [];
        },
        isConfigurable() {
            return this.cadence.unit !== 'never';
        },
        defaultValue() {
            return { unit: 'never', value: null, startDate: null };
        },
        formattedNextCadenceDate() {
            if (this.nextCadenceDateRaw) {
                return this.$filters.dateTimeMedium(
                    this.nextCadenceDateRaw,
                    this.currentLocale,
                );
            }

            return null;
        },
        transformedCadence() {
            let { value, unit, startDate } = this.cadence;
            return {
                unit,
                value: value ? parseInt(value) : null,
                startDate,
            };
        },
        nextCadenceDateRaw() {
            return this.cadenceInformation?.date;
        },
        errorMessage() {
            return (
                this.cadenceInformation?.reason ??
                this.trans(this.nextCadenceDateMessageTranslationKeyNever)
            );
        },
    },
    watch: {
        modelValue: {
            handler: function (newValue, oldValue) {
                newValue = newValue ?? this.defaultValue;
                this.cadence = {
                    ...newValue,
                    value: newValue?.value?.toString(),
                };
            },
            deep: true,
            immediate: true,
        },
        updateDueDate(newValue) {
            if (!!newValue) {
                this.computeNextCadenceDate(this.transformedCadence);
            }
        },
    },
    mounted() {
        if (this.cadence.unit !== 'never') {
            this.computeNextCadenceDate(this.transformedCadence);
        }
    },
    methods: {
        updateCadenceUnit(unit) {
            if (unit === 'never') {
                this.cadence.value = null;
                this.cadence.startDate = null;
            } else {
                this.cadence.value = this.everyOptions.first()?.value ?? null;
                this.cadence.startDate =
                    this.cadence.startDate ?? DateTime.now();
            }

            this.emitChanges();
        },
        emitChanges() {
            this.computeNextCadenceDate(this.transformedCadence);
            this.$emit('update:modelValue', this.transformedCadence);
        },
        computeNextCadenceDate(cadence) {
            if (!this.nextDueDateRoute || this.isLoading) {
                return;
            }

            let startDate = cadence.startDate;
            if (!!startDate && typeof startDate === 'string') {
                startDate = DateTime.fromISO(startDate);
            }

            this.isLoading = true;
            const params = {
                ...cadence,
                startDate: startDate?.toFormat('yyyy-MM-dd'),
            };

            $http
                .get(this.nextDueDateRoute, { params })
                .then((resp) => {
                    this.cadenceInformation = resp.data;
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>
